/**
 * المرحلة 1.35 — the child file as the screens read it: who sees the tab, the
 * child's age as the paper writes it, the growth chart's frame and points, and
 * the new row's body. Backend/routes/childFile.js is the boundary; this only
 * keeps the screens from offering what the server would refuse.
 */

import { MAX_MONTHS, PERCENTILES, curve, lmsAt, sexCode, valueAt } from './growthData';
import { PERMISSIONS } from './rbac';

const P = PERMISSIONS;

/** The chart's three measures: the growth row's key and the CDC table it is read against. */
export const MEASURES = Object.freeze([
  Object.freeze({
    key: 'length',
    chart: 'length',
    field: 'lengthCm',
    unit: 'cm',
  }),
  Object.freeze({
    key: 'weight',
    chart: 'weight',
    field: 'weightKg',
    unit: 'kg',
  }),
  Object.freeze({ key: 'head', chart: 'head', field: 'headCm', unit: 'cm' }),
]);

/**
 * The cover, the admission record, the growth chart and the notes, as the
 * booklet orders them; then the printed consents, where the stay booklet is on.
 */
export const SHEETS = Object.freeze(['cover', 'admission', 'growth', 'notes', 'consents']);

/** Is this file a child's? Marked a minor, or born under eighteen years ago. */
export function isChild(patient, now = new Date()) {
  if (!patient) return false;
  if (patient.isMinor) return true;
  const born = Date.parse(`${String(patient.dateOfBirth || '').slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(born)) return false;
  return now.getTime() - born < 18 * 365.25 * 86400000;
}

/**
 * Does this session see the child file of this patient? The policy is on, the
 * file is a child's, and the reader is the doctor (doctor_only) or anyone who
 * reads the file (doctor_and_desk).
 * @param {{ enabled: string, showTo: string }} policy  centerPolicy(locale).childFile
 * @param {(p: string) => boolean} can
 */
export function seesChildFile(policy, can, patient) {
  if (!policy || policy.enabled !== 'yes' || !isChild(patient)) return false;
  if (policy.showTo === 'doctor_only') return can(P.PRESCRIPTION_WRITE);
  return can(P.PATIENT_RECORD_VIEW) || can(P.PATIENT_FILE_MANAGE);
}

/** Years, months and days between a date of birth and now, or null. */
export function ageParts(dateOfBirth, now = new Date()) {
  const text = String(dateOfBirth || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null;
  const [y, m, d] = text.split('-').map(Number);
  let years = now.getFullYear() - y;
  let months = now.getMonth() + 1 - m;
  let days = now.getDate() - d;
  if (days < 0) {
    months -= 1;
    days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  if (years < 0) return null;
  return { years, months, days };
}

/**
 * The age as a sentence through `t`: days for a newborn, months under two,
 * then years and months. «١٢ يوم» / «٧ شهور» / «٣ سنين و٢ شهر».
 */
export function ageLabel(dateOfBirth, t, now = new Date()) {
  const a = ageParts(dateOfBirth, now);
  if (!a) return '—';
  const totalMonths = a.years * 12 + a.months;
  if (totalMonths === 0) return t('childFile.age.days', { n: a.days });
  if (totalMonths < 24) return a.days && totalMonths < 3 ? t('childFile.age.monthsDays', { m: totalMonths, d: a.days }) : t('childFile.age.months', { n: totalMonths });
  return a.months ? t('childFile.age.yearsMonths', { y: a.years, m: a.months }) : t('childFile.age.years', { n: a.years });
}

/** One measure's points from the growth rows: [{ ageMonths, value, at }] on the chart's span. */
export function pointsOf(growth = [], key) {
  return growth.filter((r) => r[key] !== null && r[key] !== undefined && r.ageMonths !== null && r.ageMonths <= MAX_MONTHS).map((r) => ({ ageMonths: r.ageMonths, value: Number(r[key]), at: r.at }));
}

/**
 * The frame of one panel: the 5th line at birth to the 95th at 36 months,
 * widened to every point (a premature baby sits under the 5th), in round steps.
 * @returns {{ min: number, max: number, step: number }}
 */
export function chartRange(chart, sex, points = []) {
  const s = sexCode(sex) || 1;
  const values = [valueAt(chart, s, 0, 5), valueAt(chart, s, MAX_MONTHS - 0.5, 95), ...points.map((p) => p.value)].filter((v) => Number.isFinite(v));
  const step = chart === 'weight' ? 1 : 5;
  const min = Math.max(0, Math.floor((Math.min(...values) - step / 2) / step) * step);
  const max = Math.ceil((Math.max(...values) + step / 2) / step) * step;
  return { min, max, step };
}

/** The percentile lines of a panel, as [age, value] points (the paper's printed curves). */
export function percentileLines(chart, sex) {
  const s = sexCode(sex) || 1;
  return PERCENTILES.map((p) => ({ p, points: curve(chart, s, p) }));
}

/** How a percentile reads: inside the band, near its edge, or outside it. */
export function percentileTone(p) {
  if (p === null || p === undefined) return 'none';
  if (p < 3 || p > 97) return 'out';
  if (p < 10 || p > 90) return 'edge';
  return 'in';
}

/** Is a reading on the chart (an age it covers, a sex it knows)? */
export const onChart = (chart, sex, ageMonths) => Boolean(sexCode(sex) && lmsAt(chart, sexCode(sex), ageMonths));

/** The new-row form, empty, dated today. */
export function blankGrowth(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return {
    at: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    weightKg: '',
    lengthCm: '',
    headCm: '',
    comment: '',
  };
}

/** What the server is sent for the new row: the filled measures only, as numbers. */
export function growthPayload(form) {
  const out = { at: form.at || undefined };
  for (const m of MEASURES) {
    const raw = String(form[m.field] ?? '')
      .trim()
      .replace(/[٠-٩]/g, (c) => '٠١٢٣٤٥٦٧٨٩'.indexOf(c))
      .replace('٫', '.')
      .replace(',', '.');
    if (raw) out[m.field] = Number(raw);
  }
  const comment = String(form.comment || '').trim();
  if (comment) out.comment = comment;
  return out;
}

/** Is the form worth sending? A date and at least one measure. */
export const growthReady = (form) => Boolean(form.at) && MEASURES.some((m) => String(form[m.field] ?? '').trim() !== '');

/** The birth block's fields, in the order the admission record and the growth sheet print them. */
export const BIRTH_FIELDS = Object.freeze([
  Object.freeze({ key: 'birthTime', kind: 'time' }),
  Object.freeze({ key: 'birthPlace', kind: 'text' }),
  Object.freeze({ key: 'birthDoctor', kind: 'text' }),
  Object.freeze({
    key: 'deliveryType',
    kind: 'choice',
    choices: ['natural', 'cesarean', 'other'],
  }),
  Object.freeze({ key: 'gestationWeeks', kind: 'number', min: 20, max: 45 }),
  Object.freeze({
    key: 'feedingType',
    kind: 'choice',
    choices: ['breast', 'formula', 'mixed'],
  }),
  Object.freeze({ key: 'birthWeightKg', kind: 'number', min: 0.3, max: 7 }),
  Object.freeze({ key: 'birthLengthCm', kind: 'number', min: 20, max: 70 }),
  Object.freeze({ key: 'birthHeadCm', kind: 'number', min: 15, max: 50 }),
  Object.freeze({ key: 'motherHeightCm', kind: 'number', min: 100, max: 220 }),
  Object.freeze({ key: 'fatherHeightCm', kind: 'number', min: 100, max: 230 }),
]);

/** The guardian's block the child file writes beside the birth block. */
export const GUARDIAN_FIELDS = Object.freeze(['guardianName', 'guardianRelation', 'guardianPhone', 'guardianNationalId', 'motherName', 'address']);

/** The edit form from the file's profile. */
export function profileForm(profile = {}) {
  const out = {};
  for (const f of BIRTH_FIELDS) out[f.key] = profile[f.key] ?? '';
  for (const key of GUARDIAN_FIELDS) out[key] = profile[key] ?? '';
  return out;
}

/** What `PUT /api/patients/:id/child` is sent: the birth block as `profile`, the guardian's beside it. */
export function profilePayload(form) {
  const profile = {};
  for (const f of BIRTH_FIELDS) {
    const v = form[f.key];
    profile[f.key] = v === '' || v === undefined ? null : f.kind === 'number' ? Number(v) : v;
  }
  const out = { profile };
  for (const key of GUARDIAN_FIELDS) out[key] = form[key] === '' ? null : form[key];
  return out;
}
